import { observer } from "@legendapp/state/react";
import { AnimatePresence, motion } from "motion/react";
import {
    PropsWithChildren,
    RefObject,
    useEffect,
    useRef,
    useState
} from "react";
import { ReactComponent as CloseAltIcon } from "./../../../assets/icons/close-alt.svg";
import { ReactComponent as InfoIcon } from "./../../../assets/icons/info.svg";
import "./Modal.scss";

export enum IModalType {
    DEFAULT = 'default',
    ERROR = 'error'
}

export enum IModalBtns {
    SUCCESS = 'success',
    ERROR = 'error',
    DEFAULT = 'default',
    MUTE = 'mute',
    SHORT = 'short',
    DISABLED = 'disabled'
}

export interface IModalFooterBtns {
    id: number,
    type?: IModalBtns[],
    title: string,
    actions: () => void
}

export interface IModal {
    customClass?: string,
    title: string,
    customBtns?: any,
    footerBtns?: IModalFooterBtns[];
    toggleVisibility: boolean,
    type?: IModalType,
    onToggle?: (state: boolean) => void
}

const Modal = observer(({ children, customClass, title, onToggle, toggleVisibility, type, footerBtns, customBtns }: PropsWithChildren<IModal>) => {
    const [isVisible, setIsVisible] = useState(false);

    const modalType = determineType();

    const element: RefObject<HTMLDivElement> = useRef(null);

    useEffect(() => {
        if (onToggle !== undefined &&
            !toggleVisibility &&
            toggleVisibility !== isVisible) {
            onToggle(toggleVisibility);
        }

        setIsVisible(toggleVisibility);
    }, [isVisible, onToggle, toggleVisibility]);

    return (
        <AnimatePresence>
            {isVisible &&
                <motion.div
                    ref={element}
                    className={`modal ${modalType} ${customClass ? customClass : ''}`}
                    contentEditable={false}
                    initial={{ opacity: 0, y: -50 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -50 }}
                    transition={{ duration: 0.45 }}
                >
                    <aside className="modal__content">
                        <header className="modal__header">
                            <span className="modal__icon">
                                <InfoIcon />
                            </span>

                            <h3 className="modal__title">
                                {title}
                            </h3>

                            <button type="button" className="modal__close btn--reset" onClick={handleClose}>
                                <CloseAltIcon />
                            </button>
                        </header>

                        <div className="modal__entry">
                            {children}
                        </div>

                        {
                            !customBtns && footerBtns && footerBtns.length ?
                                <div className="modal__footer d-flex">
                                    {
                                        footerBtns.map(({ id, actions, title, type }) => (
                                            <button key={id}
                                                type="button"
                                                className={`modal__footer-action btn ${type && type.length ? type.map(item => 'btn--' + item).join(' ') : ''}`}
                                                onClick={actions}>
                                                {title}
                                            </button>
                                        ))
                                    }
                                </div> : null
                        }

                        {
                            customBtns && !footerBtns ? customBtns : null
                        }

                    </aside>

                    <button type="button" className="modal__mask btn--reset" onClick={handleClose} />

                </motion.div>
            }
        </AnimatePresence >

    )

    function determineType() {
        switch (type) {
            case IModalType.ERROR: {
                return 'modal--error'
            }

            default: return ''
        }
    }

    function handleClose() {
        if (onToggle) {
            onToggle(!isVisible);
        }

        return setIsVisible(!isVisible);
    }
})

export default Modal;
